/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { startOfDay } from 'date-fns';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import FormBox from '../../common/FormBox/FormBox';
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup';
import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import BaseInput from 'components/common/BaseInput';
import IconTooltipTextField from 'components/common/IconTooltipTextField';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { useAuth } from 'contexts/ProtectedRoute';
import { RELATED_FROM_QUERY_KEY } from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CLIENTVIEW/components/ClientProfile/components/relatedProfile';

import { fetchOptions, fetchOptionsWithParameter } from './utils';
import api from 'api';
import { EARLIEST_BIRTH_DATE } from 'data/constants/constants';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    chosenCountryName,
    countryNameOptions,
    enabledCountryNames,
    rowCountryEnabled,
} from 'utils/countries';
import { isSalesPersonRole } from 'data/constants/roles';
import useTranslate from 'hooks/useTranslate';
import { ReactComponent as InfoIcon } from 'assets/icons/information.svg';
import { RootState } from 'redux/store';
import type { CitizenshipInfo } from 'types';
import { formatNumber } from 'utils/formatting';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';
import {
    omitHasSpouse,
    parseHasSpouseFromPayload,
    parseSpouseUserIdFromPayload,
    persistHasSpouseDraft,
    persistSpouseLifecycle,
} from 'utils/hasSpouse';
import { shouldShowResidencyProof } from 'utils/residencyProof';
import { isActingAsSpouseProfile } from 'utils/selectedAccount';

export interface ValidationMsgPI {
    birth_date: string;
    gender: string;
    marital_status: string;
    country_of_residence: string;
    region_of_residence: string;
    residency_status: string;
    has_residency_proof: string;
    residence_since: string;
    driver_license: string;
    class_2_with_airbrake: string;
    liquid_assets: string;
    net_worth: string;
    income: string;
    criminal_record: string;
}

export const initValidationMsgPI: ValidationMsgPI = {
    birth_date: '',
    gender: '',
    marital_status: '',
    country_of_residence: '',
    region_of_residence: '',
    residency_status: '',
    has_residency_proof: '',
    residence_since: '',
    driver_license: '',
    class_2_with_airbrake: '',
    liquid_assets: '',
    net_worth: '',
    income: '',
    criminal_record: '',
};

export interface PersonalInfoData {
    birth_date: Date | null;
    gender: string;
    marital_status: string;
    country_of_residence: string | null;
    region_of_residence: string | null;
    residency_status: string | null;
    has_residency_proof: boolean | null;
    residence_since: Date | null;
    driver_license: string | null;
    class_2_with_airbrake: boolean | null;
    liquid_assets: string;
    net_worth: string;
    income: string;
    criminal_record: boolean;
}

export const initialStatePI: PersonalInfoData = {
    birth_date: null,
    gender: '',
    marital_status: '',
    country_of_residence: '',
    region_of_residence: '',
    residency_status: '',
    has_residency_proof: null,
    residence_since: null,
    driver_license: '',
    class_2_with_airbrake: null,
    liquid_assets: '',
    net_worth: '',
    income: '',
    criminal_record: false,
};

interface PrevVisaData {
    name: string | null;
    start: Date | null;
    end: Date | null;
    country: string | null;
    region: string | null;
}

const initialStatePV: PrevVisaData = {
    name: '',
    start: null,
    end: null,
    country: '',
    region: '',
};

interface Msg {
    name: string;
    country: string;
    start: string;
    end: string;
    region: string;
}

const initMsg: Msg = {
    name: '',
    country: '',
    start: '',
    end: '',
    region: '',
};

export interface ValidationMsgUser {
    firstname: string;
    lastname: string;
    middlename: string;
    email: string;
    phone_number: string;
    password: string;
    repeatPassword: string;
}

export default function AdditionalDetails() {
    const { translate } = useTranslate();
    const { codes, hasCanada } = useEnabledCountries();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const [personalInfo, setPersonalInfo] = useState<PersonalInfoData>(initialStatePI);
    const [citizenships, setCitizenships] = useState<CitizenshipInfo[] | null>(null);
    const [citizenshipsLoaded, setCitizenshipsLoaded] = useState(false);
    const [isPersonalInfo, setIsPeresonalInfo] = useState<boolean>(false);
    const [validationMsgPI, setValidationMsgPI] = useState<ValidationMsgPI>(initValidationMsgPI);
    const [hasSpouse, setHasSpouse] = useState<string>('No');
    const [savedHasSpouse, setSavedHasSpouse] = useState<boolean>(false);
    const [unlinkSpouseOpen, setUnlinkSpouseOpen] = useState(false);
    const unlinkSpouseResolver = useRef<((confirmed: boolean) => void) | null>(null);
    const [, setHasFamily] = useState<string>('No');
    const [isFamilyInfo, setIsFamilyInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [prevVisaInfo, setPrevVisaInfo] = useState<PrevVisaData[]>([]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([]);
    // const [visaPrograms, setVisaPrograms] = useState<string[]>([]);
    const [countryOptions, setCountryOptions] = useState<string[]>([]);
    const [regionOptions, setRegionOptions] = useState({} as { [key: string]: string[] });
    const [residencyOptions, setResidencyOptions] = useState<string[]>([]);
    // const { data } = useSelector((state: RootState) => state.expertiseList);
    // const dispatch = useDispatch<AppDispatch>();
    useEffect(() => {
        getCountryOptions();
        getRegionOptions();
    }, []);

    const getCountryOptions = () =>
        fetchOptions(api?.assessment_options.getCountryOptions, setCountryOptions);
    const getRegionOptions = () =>
        fetchOptions(api?.assessment_options.getRegionOptions, setRegionOptions);

    const getResidencyOptions = (country: string) => {
        fetchOptionsWithParameter(
            api?.assessment_options.getResidencyOptions,
            setResidencyOptions,
            country
        );
    };

    const [progList, setProgList] = useState<any>();

    useEffect(() => {
        let cancelled = false;
        const loadPrograms = async () => {
            const names = enabledCountryNames(codes);
            if (names.length === 0) {
                setProgList([]);
                return;
            }
            try {
                const responses = await Promise.all(
                    names.map((country) => api.assessment_options.getPrevVisaOptions(country))
                );
                if (cancelled) return;
                const programArray = [
                    ...new Set(
                        responses.flatMap((response) =>
                            ((response.data as string[]) ?? []).filter(Boolean)
                        )
                    ),
                ];
                setProgList(programArray);
            } catch (e) {
                console.error(e);
            }
        };
        loadPrograms();
        return () => {
            cancelled = true;
        };
    }, [codes]);

    // Note: observing if this is depreciated

    // useEffect(() => {
    //     prepareVisaPrograms<string>('Canada');
    // }, [data]);

    // const prepareVisaPrograms = <T,>(key: T) => {
    //     if (data && key) {
    //         const currentCountryPrograms: string[] = data[key as keyof typeof data];
    //         const withoutEmptyValue = currentCountryPrograms.filter((program) => program !== '');
    //         const onlyUniqueValues = [...new Set(withoutEmptyValue)];
    //         setVisaPrograms(onlyUniqueValues);
    //         console.log(onlyUniqueValues)
    //         return;
    //     }
    //     setVisaPrograms([]);
    // };

    const is_in_destination_country = ['Australia', 'Canada', 'United Kingdom'].includes(
        personalInfo.country_of_residence as string
    );
    const showResidencyProof = shouldShowResidencyProof(
        hasCanada,
        personalInfo.country_of_residence,
        citizenships
    );

    useEffect(() => {
        if (isPersonalInfo && isFamilyInfo && citizenshipsLoaded) {
            setIsLoading(false);
        }
    }, [isPersonalInfo, isFamilyInfo, citizenshipsLoaded]);

    const loadPersonalInfo = async (data: PersonalInfoData | Record<string, unknown> | null) => {
        const hasSpouseFlag = parseHasSpouseFromPayload(data);
        const spouseUserId = parseSpouseUserIdFromPayload(data);
        if (hasSpouseFlag !== undefined) {
            setSavedHasSpouse(hasSpouseFlag);
            setHasSpouse(hasSpouseFlag ? 'Yes' : 'No');
            persistSpouseLifecycle(hasSpouseFlag, spouseUserId);
        } else if (spouseUserId) {
            persistSpouseLifecycle(undefined, spouseUserId);
        }
        const personalFields = data ? omitHasSpouse(data as object) : {};
        if (personalFields && Object.keys(personalFields).length) {
            const next = { ...initialStatePI, ...(personalFields as Partial<PersonalInfoData>) };
            setPersonalInfo(next);
            if (next.country_of_residence) {
                getResidencyOptions(next.country_of_residence as string);
            }
        }
        setIsPeresonalInfo(true);
    };
    const loadPrevVisaInfo = async (data: PrevVisaData[]) => {
        if (data && data.length | Object.keys(data).length) {
            const tmp = new Array(data.length).fill(initMsg);
            setValidationMsg(tmp);
            setPrevVisaInfo(data);
        }
    };

    const handleUpdateMe = async (field: string, value: string | any) => {
        validate(field, value);
        let temp: PersonalInfoData;
        if (field === 'country_of_residence') {
            temp = {
                ...personalInfo,
                [field]: value,
                residency_status: '',
                region_of_residence: '',
            };
            getResidencyOptions(value);
        } else {
            temp = { ...personalInfo, [field]: value };
        }
        setPersonalInfo(temp);
    };

    const handleDateUpdateMe = (field: string, newDate: Date | null) => {
        // User cleared the field
        if (newDate === null) {
            validate(field, '');
            setValidationMsgPI((prev) => ({
                ...prev,
                [field]: t('requiredField', { lng: language }),
            }));
            setPersonalInfo((prev) => ({
                ...prev,
                [field]: null,
            }));
            return;
        }

        // Invalid Date (unparseable) — keep existing value so day/month are not wiped
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            setValidationMsgPI((prev) => ({
                ...prev,
                [field]: 'Invalid date format',
            }));
            return;
        }

        // Keep controlled value in sync while typing so day/month are preserved
        // across intermediate year keystrokes (1 → 19 → 199 → 1990) under date-fns v4 / MUI X v9.
        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            setValidationMsgPI((prev) => ({
                ...prev,
                [field]: 'Invalid date format',
            }));
            setPersonalInfo((prev) => ({
                ...prev,
                [field]: extractDateFormat,
            }));
            return;
        }

        validate(field, extractDateFormat);
        setValidationMsgPI((prev) => ({
            ...prev,
            [field]: '',
        }));
        setPersonalInfo((prev) => ({
            ...prev,
            [field]: extractDateFormat,
        }));
    };

    const handleUpdateCriminalRecord = (value: string) => {
        let returnVal = false;
        if (value === t('yes', { lng: language })) {
            returnVal = true;
        } else if (value === t('no', { lng: language })) {
            returnVal = false;
        }
        const temp: PersonalInfoData = { ...personalInfo, criminal_record: returnVal };
        setPersonalInfo(temp);
    };

    const handleUpdateClass2 = (value: string) => {
        let returnVal = false;
        if (value === t('yes', { lng: language })) {
            returnVal = true;
        } else if (value === t('no', { lng: language })) {
            returnVal = false;
        }
        const temp: PersonalInfoData = { ...personalInfo, class_2_with_airbrake: returnVal };
        setPersonalInfo(temp);
    };

    const handleUpdateHasSpouse = (value: string) => {
        if (value === t('yes', { lng: language })) {
            setHasSpouse('Yes');
            persistHasSpouseDraft(true);
        } else if (value === t('no', { lng: language })) {
            setHasSpouse('No');
            persistHasSpouseDraft(false);
        }
    };

    const confirmUnlinkSpouseIfNeeded = () => {
        if (!(savedHasSpouse && hasSpouse === 'No')) {
            return Promise.resolve(true);
        }
        return new Promise<boolean>((resolve) => {
            unlinkSpouseResolver.current = resolve;
            setUnlinkSpouseOpen(true);
        });
    };

    const handleUnlinkSpouseConfirm = () => {
        setUnlinkSpouseOpen(false);
        unlinkSpouseResolver.current?.(true);
        unlinkSpouseResolver.current = null;
    };

    const handleUnlinkSpouseCancel = () => {
        setUnlinkSpouseOpen(false);
        unlinkSpouseResolver.current?.(false);
        unlinkSpouseResolver.current = null;
    };

    const handleAdditionalDetailsSaved = (results: Array<{ tableName: string; data: unknown }>) => {
        const saved = results.find((result) => result.tableName === 'AdditionalDetails');
        const flag = parseHasSpouseFromPayload(saved?.data);
        const spouseUserId = parseSpouseUserIdFromPayload(saved?.data);
        const next = flag ?? hasSpouse === 'Yes';
        setSavedHasSpouse(next);
        setHasSpouse(next ? 'Yes' : 'No');
        persistSpouseLifecycle(next, spouseUserId);
    };

    // const handleUpdateHasFamily = (value: string) => {
    //     if (value === t('yes', { lng: language })) {
    //         setHasFamily('Yes');
    //     } else if (value === t('no', { lng: language })) {
    //         setHasFamily('No');
    //         handleDeleteFamily();
    //     }
    // };

    //temp path change

    // useEffect(() => {
    //     window.localStorage.setItem('hasFamily', JSON.stringify(hasFamily));
    //     window.dispatchEvent(new Event('storage'));
    // }, [hasFamily]);

    const { role } = useAuth();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const candidate_id = Number(params.id);
    const viewingRelatedProfile =
        Boolean(searchParams.get(RELATED_FROM_QUERY_KEY)) || isActingAsSpouseProfile();

    const isSalesPerson = isSalesPersonRole(role);

    useEffect(() => {
        if (isAuthenticated) {
            setPersonalInfo(initialStatePI);
            setCitizenships(null);
            if (isSalesPerson) {
                api.tableInput.fetchCandidateInputSales(
                    loadPersonalInfo,
                    'AdditionalDetails',
                    candidate_id
                );
                api.tableInput.fetchCandidateInputSales(
                    loadPrevVisaInfo,
                    'PreviousVisa',
                    candidate_id
                );
            } else {
                api.tableInput.fetchCandidateInput(loadPersonalInfo, 'AdditionalDetails');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, candidate_id]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        if (!hasCanada) {
            setCitizenshipsLoaded(true);
            return;
        }
        setCitizenshipsLoaded(false);
        const loadCitizenships = (citizenshipData: CitizenshipInfo[] | null) => {
            setCitizenships(citizenshipData);
            setCitizenshipsLoaded(true);
        };
        if (isSalesPerson) {
            api.tableInput.fetchCandidateInputSales(loadCitizenships, 'Citizenship', candidate_id);
        } else {
            api.tableInput.fetchCandidateInput(loadCitizenships, 'Citizenship');
        }
    }, [candidate_id, hasCanada, isAuthenticated, isSalesPerson]);

    //load fam
    const setFamilyData = async (data: any) => {
        if (data && data.length | Object.keys(data).length) {
            if (data[0].firstname !== null) {
                setHasFamily('Yes');
            }
        }
        if (data == null || data) {
            setIsFamilyInfo(true);
        } else {
            setIsFamilyInfo(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsFamilyInfo(false);
            setHasFamily('No');
            if (isSalesPerson) {
                api.tableInput.fetchCandidateInputSales(
                    setFamilyData,
                    'FamilyMember',
                    candidate_id
                );
            } else {
                api.tableInput.fetchCandidateInput(setFamilyData, 'FamilyMember');
            }
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, candidate_id]);

    // const handleDeleteFamily = () => {
    //     try {
    //         api.tableInput.deleteInfo(famMemberTablename, candidate_id);
    //     } catch (err) {
    //         console.log(err);
    //     }
    // };

    // FIELD VALIDATION
    const validate = async (
        field: string,
        value: string,
        valid?: boolean,
        idx?: number,
        section?: string
    ) => {
        let validated = true;

        //personalInfo
        if (!section) {
            let tempMsgPI = validationMsgPI;
            const tempMsgPrevV = [...validationMsg];
            const isIncompleteStoredDate = (raw: unknown): boolean => {
                if (raw == null || raw === '') return false;
                const d = parseStoredDate(raw as Date | string);
                return !d || !isFullDateFormat(d);
            };

            if (!field && !value) {
                for (const key of Object.keys(initialStatePI)) {
                    const value = personalInfo[key as keyof PersonalInfoData];
                    let msg = '';
                    if (key === 'gender') continue;
                    //   if (key === 'country_of_residence') continue;
                    if (key === 'driver_license') continue;
                    if (key === 'criminal_record') continue;
                    if (key === 'has_residency_proof' && !showResidencyProof) continue;
                    if (
                        key === 'class_2_with_airbrake' &&
                        personalInfo.class_2_with_airbrake === null
                    ) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                    }
                    // if (key === 'residency_status') continue;
                    if (key === 'region_of_residence' && !is_in_destination_country) continue;
                    else if (value === '' || value === null) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                    } else if (
                        (key === 'birth_date' || key === 'residence_since') &&
                        isIncompleteStoredDate(value)
                    ) {
                        // Reject intermediate years stored while typing (e.g. 0001-03-15)
                        msg = 'Invalid date format';
                        validated = false;
                    }
                    tempMsgPI = { ...tempMsgPI, [key]: msg };
                }
            } else {
                let msg = '';
                if (value === '' || value === null) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                    tempMsgPI = { ...tempMsgPI, [field]: msg };
                } else if (
                    (field === 'birth_date' || field === 'residence_since') &&
                    isIncompleteStoredDate(value)
                ) {
                    msg = 'Invalid date format';
                    validated = false;
                    tempMsgPI = { ...tempMsgPI, [field]: msg };
                } else {
                    tempMsgPI = { ...tempMsgPI, [field]: msg };
                }
            }
            setValidationMsgPI(tempMsgPI);

            for (let i = 0; i < prevVisaInfo.length; i++) {
                for (const key of Object.keys(initialStatePV)) {
                    const value = prevVisaInfo[i][key as keyof PrevVisaData];
                    let msg = '';
                    if (
                        key === 'region' &&
                        !rowCountryEnabled(codes, prevVisaInfo[i].country, 'AU')
                    ) {
                        continue;
                    }
                    if (!value) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        tempMsgPrevV[i] = { ...tempMsgPrevV[i], [key]: msg };
                    } else if (
                        (key === 'start' || key === 'end') &&
                        isIncompleteStoredDate(value)
                    ) {
                        msg = 'Invalid date format';
                        validated = false;
                        tempMsgPrevV[i] = { ...tempMsgPrevV[i], [key]: msg };
                    }
                }
                setValidationMsg(tempMsgPrevV);
            }
            return validated;
        }
    };

    const validateNumberMe = (field: string, value: string | null) => {
        let validated = true;
        let tempMsgPI = validationMsgPI;
        let msg = '';
        if (value && isNaN(Number(value.replaceAll(',', '')))) {
            msg = t('shouldBeANumber', { lng: language });
            validated = false;
        }
        tempMsgPI = { ...tempMsgPI, [field]: msg };
        setValidationMsgPI(tempMsgPI);
        return validated;
    };

    const handleNumberUpdateMe = (field: string, value: string) => {
        if (validateNumberMe(field, value)) {
            value = value ? value : '0';
            const valueWithoutSeparators = value
                .replaceAll(' ', '')
                .replaceAll('.', '')
                .split(',')
                .join('');
            const temp = { ...personalInfo, [field]: valueWithoutSeparators };
            setPersonalInfo(temp);
        }
    };

    //Prev Visas

    const handleDateUpdate = (idx: number, field: string, newDate: Date | null) => {
        // User cleared the field
        if (newDate === null) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsg(temp);
            const tmp = [...prevVisaInfo];
            tmp[idx] = { ...tmp[idx], [field]: null };
            setPrevVisaInfo(tmp);
            return;
        }

        // Invalid Date (unparseable) — keep existing value so month is not wiped
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            return;
        }

        // Keep controlled value in sync while typing so month is preserved
        // across intermediate year keystrokes under date-fns v4 / MUI X v9.
        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            const tmp = [...prevVisaInfo];
            tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
            setPrevVisaInfo(tmp);
            return;
        }

        const temp = [...validationMsg];
        validate(field, extractDateFormat);
        temp[idx] = { ...temp[idx], [field]: '' };
        setValidationMsg(temp);
        const tmp = [...prevVisaInfo];
        tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
        if (field === 'start') {
            tmp[idx] = { ...tmp[idx], end: null };
        }
        setPrevVisaInfo(tmp);
    };

    const validateSingleField = (idx: number, field: string, value: string | boolean | null) => {
        const temp = [...validationMsg];
        let msg = '';
        if (!value) {
            msg = t('requiredField', { lng: language });
        }
        temp[idx] = { ...temp[idx], [field]: msg };
        setValidationMsg(temp);
    };

    const handleUpdate = (idx: number, field: string, value: string | boolean | null) => {
        validateSingleField(idx, field, value);

        const temp = [...prevVisaInfo];
        temp[idx] = { ...prevVisaInfo[idx], [field]: value };
        if (field === 'country') {
            temp[idx] = { ...temp[idx], region: '' };
        }
        setPrevVisaInfo(temp);
    };

    const handleDeleteEntry = (idx: number) => {
        const temp: PrevVisaData[] = [...prevVisaInfo];
        temp.splice(idx, 1);
        setPrevVisaInfo(temp);
        const tempMsg: Msg[] = [...validationMsg];
        tempMsg.splice(idx, 1);
        setValidationMsg(tempMsg);
    };

    const handleAddEntry = () => {
        if (prevVisaInfo.length > 10) return;
        const newEntry: PrevVisaData = {
            ...initialStatePV,
            country: chosenCountryName(codes) ?? '',
        };

        const temp: PrevVisaData[] = [...prevVisaInfo, newEntry];
        setPrevVisaInfo(temp);

        const tempMsg: Msg[] = [...validationMsg, initMsg];
        setValidationMsg(tempMsg);
    };

    const checkEndDateBeforeStart = (
        endDate: Date | null,
        startDate: Date | null | undefined,
        idx: number
    ) => {
        const normalizedStartDate = startDate ? startOfDay(new Date(startDate)) : undefined;
        const normalizedEndDate = endDate ? startOfDay(endDate) : undefined;

        // Check if endDate is earlier than startDate
        if (normalizedEndDate && normalizedStartDate && normalizedEndDate < normalizedStartDate) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                end: t('endDateBeforeStartDate', { lng: language }),
            };
            setValidationMsg(temp);
            return true;
        }

        return false;
    };

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox
                    validation={validate}
                    tableNames={['AdditionalDetails', 'PreviousVisa']}
                    data={[
                        viewingRelatedProfile
                            ? omitHasSpouse(personalInfo)
                            : { ...omitHasSpouse(personalInfo), has_spouse: hasSpouse === 'Yes' },
                        prevVisaInfo,
                    ]}
                    omitHasSpouse={viewingRelatedProfile}
                    onBeforeSave={viewingRelatedProfile ? undefined : confirmUnlinkSpouseIfNeeded}
                    onAfterSave={handleAdditionalDetailsSaved}
                >
                    <S.Title>{t('additionalDetails', { lng: language })}</S.Title>
                    <S.FormContainer>
                        <S.GridSection>
                            <ToggleButtonGroup
                                options={[
                                    t('male', { lng: language }),
                                    t('female', { lng: language }),
                                ]}
                                name="gender"
                                label={t('gender', { lng: language })}
                                value={personalInfo.gender}
                                onChange={handleUpdateMe}
                                error={validationMsgPI.gender}
                            />
                            <ToggleButtonGroup
                                options={[
                                    t('married', { lng: language }),
                                    t('single', { lng: language }),
                                ]}
                                name="marital_status"
                                label={t('maritalStatus', { lng: language })}
                                value={personalInfo.marital_status}
                                onChange={handleUpdateMe}
                                error={validationMsgPI.marital_status}
                            />
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                {/* MUI X v9: inputFormat→format, renderInput→slotProps.textField */}
                                <DesktopDatePicker
                                    minDate={new Date(EARLIEST_BIRTH_DATE)}
                                    maxDate={new Date()}
                                    label={`*${t('dateOfBirth', { lng: language })}`}
                                    format="dd/MM/yyyy"
                                    // must convert to date to prevent bugs for lower local timezones
                                    value={parseStoredDate(personalInfo.birth_date)}
                                    onChange={(date: Date | null) => {
                                        handleDateUpdateMe('birth_date', date);
                                    }}
                                    slotProps={{
                                        field: { clearable: true },
                                        textField: {
                                            error: validationMsgPI.birth_date !== '',
                                            helperText: validationMsgPI.birth_date,
                                            title: t('clickOnCalendar', { lng: language }),
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Autocomplete
                                sx={{
                                    '& .MuiInputBase-input': {
                                        height: 30,
                                    },
                                }}
                                disablePortal
                                value={personalInfo.country_of_residence}
                                id="*country_of_residence"
                                options={countryOptions}
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate('country_of_residence', e.target.value);
                                }}
                                onChange={(_, value: string | null) =>
                                    handleUpdateMe('country_of_residence', value)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`*${t('countryOfResidence', { lng: language })}`}
                                        error={validationMsgPI.country_of_residence !== ''}
                                        helperText={validationMsgPI.country_of_residence}
                                    />
                                )}
                            />
                            <Autocomplete
                                hidden={!is_in_destination_country}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        height: 30,
                                    },
                                }}
                                disablePortal
                                value={personalInfo.region_of_residence}
                                id="region_of_residence"
                                options={
                                    regionOptions[personalInfo.country_of_residence as string] || []
                                }
                                onChange={(_, v: string | null) =>
                                    handleUpdateMe('region_of_residence', v)
                                }
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate('region_of_residence', e.target.value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`*${t('regionOfResidence', { lng: language })}`}
                                        error={validationMsgPI.region_of_residence !== ''}
                                        helperText={validationMsgPI.region_of_residence}
                                    />
                                )}
                            />
                            <Autocomplete
                                sx={{
                                    '& .MuiInputBase-input': {
                                        height: 30,
                                    },
                                }}
                                disablePortal
                                value={personalInfo.residency_status}
                                id="residency_status"
                                options={residencyOptions}
                                onChange={(_, v: string | null) =>
                                    handleUpdateMe('residency_status', v)
                                }
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate('residency_status', e.target.value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`*${t('statusInCountryOfResidence', {
                                            lng: language,
                                        })}`}
                                        error={validationMsgPI.residency_status !== ''}
                                        helperText={validationMsgPI.residency_status}
                                    />
                                )}
                            />
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                {/* MUI X v9: inputFormat→format, renderInput→slotProps.textField */}
                                <DesktopDatePicker
                                    views={['year', 'month']}
                                    minDate={
                                        parseStoredDate(personalInfo.birth_date) ??
                                        new Date(EARLIEST_BIRTH_DATE)
                                    }
                                    maxDate={new Date()}
                                    label={`*${t('residenceStatusSince', { lng: language })}`}
                                    format="MM/yyyy"
                                    value={parseStoredDate(personalInfo.residence_since)}
                                    onChange={(date: Date | null) => {
                                        handleDateUpdateMe('residence_since', date);
                                    }}
                                    slotProps={{
                                        field: { clearable: true },
                                        textField: {
                                            error: validationMsgPI.residence_since !== '',
                                            helperText: validationMsgPI.residence_since,
                                            title: t('clickOnCalendar', { lng: language }),
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Autocomplete
                                disablePortal
                                value={personalInfo.driver_license}
                                id="driver_license"
                                options={[
                                    '',
                                    "Don't have it",
                                    'A',
                                    'A1',
                                    'A2',
                                    'B1',
                                    'C',
                                    'C1',
                                    'D',
                                    'D1',
                                    'D2',
                                    'D3',
                                    'E',
                                ]}
                                onChange={(_, value: string | null) =>
                                    handleUpdateMe('driver_license', value)
                                }
                                renderInput={(params) => (
                                    <IconTooltipTextField
                                        {...params}
                                        label={t('driversLicense', { lng: language })}
                                        error={validationMsgPI.driver_license !== ''}
                                        helperText={validationMsgPI.driver_license}
                                        iconEnd={<InfoIcon />}
                                        endText={driverLicenseTip}
                                    />
                                )}
                            />
                        </S.GridSection>
                        <S.LanguageSection>
                            {t('noteMinAssets', { lng: language })}
                        </S.LanguageSection>
                        <S.GridSection>
                            <BaseInput
                                id="income"
                                label={`*${t('approxYearlyIncome(USD)', { lng: language })}`}
                                type="text"
                                value={formatNumber(personalInfo.income)}
                                onFocus={(e) => {
                                    validate(
                                        'income',
                                        e.target.value,
                                        undefined,
                                        undefined,
                                        'personalInfo'
                                    );
                                }}
                                onChange={(e) => {
                                    handleNumberUpdateMe('income', e.target.value);
                                }}
                                error={validationMsgPI.income !== ''}
                                helperText={validationMsgPI.income}
                            />
                            <BaseInput
                                id="net_worth"
                                label={`*${t('netWorth(USD)', { lng: language })}`}
                                type="text"
                                value={formatNumber(personalInfo.net_worth)}
                                onFocus={(e) => {
                                    validate(
                                        'net_worth',
                                        e.target.value,
                                        undefined,
                                        undefined,
                                        'personalInfo'
                                    );
                                }}
                                onChange={(e) => {
                                    handleNumberUpdateMe('net_worth', e.target.value);
                                }}
                                error={validationMsgPI.net_worth !== ''}
                                helperText={validationMsgPI.net_worth}
                            />
                            <BaseInput
                                id="funds"
                                label={`*${t('approxAvailableFunds(USD)', { lng: language })}`}
                                type="text"
                                value={formatNumber(personalInfo.liquid_assets)}
                                onFocus={(e) => {
                                    validate(
                                        'liquid_assets',
                                        e.target.value,
                                        undefined,
                                        undefined,
                                        'personalInfo'
                                    );
                                }}
                                onChange={(e) => {
                                    handleNumberUpdateMe('liquid_assets', e.target.value);
                                }}
                                error={validationMsgPI.liquid_assets !== ''}
                                helperText={validationMsgPI.liquid_assets}
                            />
                        </S.GridSection>
                        <S.GridSection justifyItems={true}>
                            {showResidencyProof && (
                                <ToggleButtonGroup
                                    customStyle={true}
                                    colForMobile={true}
                                    options={[translate('yes'), translate('no')]}
                                    name="has_residency_proof"
                                    label={translate('hasResidencyProofQuestion')}
                                    tooltip={translate('hasResidencyProofTooltip')}
                                    value={
                                        personalInfo.has_residency_proof === null
                                            ? ''
                                            : translate(
                                                  personalInfo.has_residency_proof ? 'yes' : 'no'
                                              )
                                    }
                                    onChange={(_, value) => {
                                        setPersonalInfo((prev) => ({
                                            ...prev,
                                            has_residency_proof: value === translate('yes'),
                                        }));
                                        setValidationMsgPI((prev) => ({
                                            ...prev,
                                            has_residency_proof: '',
                                        }));
                                    }}
                                    error={validationMsgPI.has_residency_proof}
                                />
                            )}
                            <ToggleButtonGroup
                                customStyle={true}
                                colForMobile={true}
                                options={[t('yes', { lng: language }), t('no', { lng: language })]}
                                name="criminal_record"
                                label={t('criminalRecord', { lng: language })}
                                value={
                                    personalInfo.criminal_record
                                        ? t('yes', { lng: language })
                                        : t('no', { lng: language })
                                }
                                onChange={(_: string, value: string) =>
                                    handleUpdateCriminalRecord(value)
                                }
                            />
                            {!viewingRelatedProfile && (
                                <ToggleButtonGroup
                                    options={[
                                        t('yes', { lng: language }),
                                        t('no', { lng: language }),
                                    ]}
                                    colForMobile={true}
                                    customStyle={true}
                                    name="spouse"
                                    label={t('travelWithSpouse', { lng: language })}
                                    value={`${t(`${hasSpouse.toLowerCase()}`, { lng: language })}`}
                                    onChange={(_: string, value: string) =>
                                        handleUpdateHasSpouse(value)
                                    }
                                />
                            )}
                            <ToggleButtonGroup
                                customStyle={true}
                                colForMobile={true}
                                options={[t('yes', { lng: language }), t('no', { lng: language })]}
                                name="class_2_with_airbrake"
                                label={t('class2withAirbrakeQuestion', { lng: language })}
                                value={
                                    personalInfo.class_2_with_airbrake === null
                                        ? ''
                                        : personalInfo.class_2_with_airbrake
                                          ? t('yes', { lng: language })
                                          : t('no', { lng: language })
                                }
                                onChange={(_: string, value: string) => handleUpdateClass2(value)}
                                error={validationMsgPI.class_2_with_airbrake}
                            />
                            {/* <ToggleButtonGroup
                                familyMember={true}
                                colForMobile={true}
                                customStyle={true}
                                options={[t('yes', { lng: language }), t('no', { lng: language })]}
                                name="family"
                                label={t('travelWithFamily', { lng: language })}
                                value={`${t(`${hasFamily.toLowerCase()}`, { lng: language })}`}
                                onChange={(_: string, value: string) =>
                                    handleUpdateHasFamily(value)
                                }
                            /> */}
                        </S.GridSection>
                    </S.FormContainer>

                    <S.ContentDivider style={{ marginBottom: '40px !important' }} />

                    <div>
                        <AddEntryFooter
                            title={t('previouslyHeldVisas', { lng: language })}
                            addLabel={t('addVisa', { lng: language })}
                            addAnotherLabel={t('addAnotherVisa', { lng: language })}
                            hasEntries={prevVisaInfo.length > 0}
                            onClick={() => handleAddEntry()}
                            titleAsSection
                        />
                        {prevVisaInfo.map((item, i) => {
                            return (
                                <S.EntryCard key={`visa-${i}`}>
                                    <div>
                                        <S.SubTitle>
                                            <>{`${t('Visa', { lng: language })} ${i + 1}`}</>
                                            <S.DeleteButton>
                                                <button
                                                    onClick={() => {
                                                        handleDeleteEntry(i);
                                                    }}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </S.DeleteButton>
                                        </S.SubTitle>
                                    </div>
                                    <S.FlexColumn>
                                        <S.FlexColumn>
                                            <S.GridSection>
                                                <Autocomplete
                                                    isOptionEqualToValue={(option, value) =>
                                                        option === value
                                                    }
                                                    fullWidth
                                                    disablePortal
                                                    id="name"
                                                    value={prevVisaInfo[i].name}
                                                    options={progList ? progList : []}
                                                    onChange={(_, value: string | null) => {
                                                        handleUpdate(i, 'name', value);
                                                    }}
                                                    renderOption={(props, option) => (
                                                        <li
                                                            {...props}
                                                            key={Date.now() + option}
                                                            style={{ margin: 0 }}
                                                        >
                                                            {option}
                                                        </li>
                                                    )}
                                                    renderInput={(params: any) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('name', {
                                                                lng: language,
                                                            })}`}
                                                            error={validationMsg[i].name !== ''}
                                                            helperText={validationMsg[i].name}
                                                        />
                                                    )}
                                                />
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    {/* MUI X v9: inputFormat→format, renderInput→slotProps.textField */}
                                                    <DesktopDatePicker
                                                        label={`*${t('startDate', {
                                                            lng: language,
                                                        })}`}
                                                        maxDate={new Date()}
                                                        views={['year', 'month']}
                                                        openTo="year"
                                                        format="MM/yyyy"
                                                        value={parseStoredDate(
                                                            prevVisaInfo[i].start
                                                        )}
                                                        onChange={(date: Date | null) => {
                                                            handleDateUpdate(i, 'start', date);
                                                        }}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            textField: {
                                                                error:
                                                                    validationMsg[i].start !== '',
                                                                helperText: validationMsg[i].start,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>

                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    {/* MUI X v9: inputFormat→format, renderInput→slotProps.textField */}
                                                    <DesktopDatePicker
                                                        views={['year', 'month']}
                                                        minDate={
                                                            parseStoredDate(
                                                                prevVisaInfo[i].start
                                                            ) ?? new Date()
                                                        }
                                                        maxDate={new Date()}
                                                        label={`*${t('endDate', {
                                                            lng: language,
                                                        })}`}
                                                        format="MM/yyyy"
                                                        openTo="year"
                                                        value={parseStoredDate(prevVisaInfo[i].end)}
                                                        onChange={(date: Date | null) => {
                                                            // Always commit the typed value first so
                                                            // intermediate/complete years stay visible
                                                            // (returning early here left e.g. 0201 on screen).
                                                            handleDateUpdate(i, 'end', date);
                                                            // Then flag end-before-start when year is complete.
                                                            if (date && isFullDateFormat(date)) {
                                                                checkEndDateBeforeStart(
                                                                    date,
                                                                    parseStoredDate(
                                                                        prevVisaInfo[i].start
                                                                    ),
                                                                    i
                                                                );
                                                            }
                                                        }}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            textField: {
                                                                error: validationMsg[i].end !== '',
                                                                helperText: validationMsg[i].end,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                                <S.FlexColumn>
                                                    <Autocomplete
                                                        disablePortal
                                                        id="country"
                                                        value={prevVisaInfo[i].country}
                                                        options={countryNameOptions(
                                                            codes,
                                                            prevVisaInfo[i].country
                                                        )}
                                                        onChange={(_, value: string | null) => {
                                                            handleUpdate(i, 'country', value);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('country', {
                                                                    lng: language,
                                                                })}`}
                                                                error={
                                                                    validationMsg[i].country !== ''
                                                                }
                                                                helperText={
                                                                    validationMsg[i].country
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </S.FlexColumn>
                                                {rowCountryEnabled(
                                                    codes,
                                                    prevVisaInfo[i].country,
                                                    'AU'
                                                ) && (
                                                    <S.FlexColumn>
                                                        <Autocomplete
                                                            disablePortal
                                                            id="region"
                                                            value={prevVisaInfo[i].region}
                                                            options={
                                                                regionOptions[
                                                                    prevVisaInfo[i]
                                                                        .country as string
                                                                ] || []
                                                            }
                                                            onChange={(_, value: string | null) => {
                                                                handleUpdate(i, 'region', value);
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={`*${t('region', {
                                                                        lng: language,
                                                                    })}`}
                                                                    error={
                                                                        validationMsg[i].region !==
                                                                        ''
                                                                    }
                                                                    helperText={
                                                                        validationMsg[i].region
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </S.FlexColumn>
                                                )}
                                            </S.GridSection>
                                        </S.FlexColumn>
                                    </S.FlexColumn>
                                </S.EntryCard>
                            );
                        })}
                    </div>
                </FormBox>
            )}
            {unlinkSpouseOpen && (
                <ConfirmationDialog
                    title={t('unlinkSpouseTitle', {
                        lng: language,
                        defaultValue: 'Remove spouse from this assessment?',
                    })}
                    content={t('unlinkSpouseMessage', {
                        lng: language,
                        defaultValue:
                            'The spouse will be removed from this assessment. Their profile data is kept and can be restored if you select Yes later. Spouse sections will be hidden.',
                    })}
                    onClose={handleUnlinkSpouseCancel}
                    onConfirm={handleUnlinkSpouseConfirm}
                />
            )}
        </>
    );
}

const driverLicenseTip = (
    <>
        <ul>
            <b>
                {' '}
                <p style={{ fontSize: '14px' }}>Category/Classification for Motorcycles</p>
            </b>
            <li>
                <b>A </b>- Motorcycle above 35 kilowatts (kw)
            </li>
            <li>
                {' '}
                <b> A1</b> - Motorcycle up to 125 cc's where the engine power does not exceed 11 kw
                (47.46horsepower)
            </li>
            <li>
                {' '}
                <b>A2 </b>- Motorcycle where the engine power does not exceed 35 kw (14.916
                horsepower)
            </li>
        </ul>{' '}
        <ul>
            <b>
                <p
                    style={{
                        fontSize: '14px',
                        marginTop: '10px',
                    }}
                >
                    Category/Classification for Other Vehicles
                </p>
            </b>
            <li>
                <b> B</b> - Vehicle up to 3.5 tons (private vehicle - that's the one most of us
                need)
            </li>
            <li>
                <b>C1</b> - Up to 12 tons
            </li>
            <li>
                <b>C</b> - Above 12 tons
            </li>
            <li>
                <b>C, E </b>- Semi-trailer
            </li>
            <li>
                <b>B, D</b> - Bus and private
            </li>
            <li>
                <b>C1, D </b>- Bus and freight
            </li>
            <li>
                <b>D1 </b>- Taxi and minibus up to 5 tons
            </li>
            <li>
                <b>D2</b> - Public transport minibus up to 5 tons
            </li>
            <li>
                <b>D3 </b>- Private transport minibus up to 5 tons
            </li>
        </ul>
    </>
);
